import Debug from '../../helpers/debug.js';
import ora from 'ora';
import Table from 'cli-table';
import descriptions from '../../helpers/descriptions.js';
import { pr, printJSON, themed } from '../../helpers/print.js';
import {
  getTenants,
  getLinkedSources,
  getSupportedFlow,
  getLinkedConnectors,
  patchLinkedConnectorConfig,
  patchLinkedSourceConfig,
} from '../../helpers/api.js';
import { buildChunks } from '../../helpers/utils.js';
import { existsSync, readFileSync } from 'fs';

const debug = Debug('commands:tenants:update-config');

const command = 'update-config';

const desc = 'Update the config of a specific connector and flow for all tenants';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('flow', descriptions.options['flow'].config)
    .demandOption('flow', descriptions.options['flow'].demandText)
    .option('connector', descriptions.options['connector'].config)
    .demandOption('connector', descriptions.options['connector'].demandText)
    .option('configFilePath', descriptions.options['configFilePath'].config)
    .demandOption('configFilePath', descriptions.options['configFilePath'].demandText);
};

const processTenant = async ({ debug, baseUri, apikey, env, flow, tenant, connectorId, config, isV2Flow }) => {
  const lookupKey = isV2Flow ? 'id' : 'tap';

  // Get all linked connectors/sources of the tuple (env, flow, tenant)
  const linkedConnectors = isV2Flow
    ? await getLinkedConnectors({
      debug,
      baseUri,
      env,
      flow,
      tenant,
      apikey,
      config: true,
    })
    : await getLinkedSources({
      debug,
      baseUri,
      env,
      flow,
      tenant,
      apikey,
      config: true,
    });

  const connector = linkedConnectors.find((c) => c[lookupKey] === connectorId);

  if (!connector) return null;

  const patchFunction = isV2Flow ? patchLinkedConnectorConfig : patchLinkedSourceConfig;

  try {
    await patchFunction({
      debug,
      baseUri,
      env,
      flow,
      tenant,
      apikey,
      connectorId,
      config,
    });
  } catch (e) {
    console.log(themed(`Error patching: ${tenant}. ${e}`, 'secondary'))
  }

  return tenant;
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, flow, connector, configFilePath } = argv;

  let message;
  let spinner = ora();

  try {
    if (!configFilePath) {
      throw new Error('Config file path not provided');
    }

    if (!configFilePath.endsWith('.json')) {
      throw new Error('Config file must have .json extension');
    }

    if (!existsSync(configFilePath)) {
      throw new Error('Config file not found');
    }

    const config = JSON.parse(readFileSync(configFilePath, { encoding: 'utf-8' }));

    message = themed(`Retrieving tenants for environment ${themed(env, 'info')}`);

    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const [allTenants, supportedFlow] = await Promise.all([
      getTenants({
        debug,
        baseUri: hg.clientApiBaseUri,
        apikey,
        env,
      }),
      getSupportedFlow({
        debug,
        baseUri: hg.clientApiBaseUri,
        apikey,
        env,
        flow,
      }),
    ]);

    const isV2Flow = supportedFlow?.version === 2;

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    const tenantsUpdated = [];

    message = themed(`Updating config for connector ${themed(connector, 'info')} and flow ${themed(flow, 'info')}`);

    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    // Break the tenants array in to an array of arrays so we can make
    // parallel calls and make it faster
    for (const tenants of buildChunks(allTenants)) {
      const maybeTenantsUpdated = await Promise.all(
        tenants.map((tenant) =>
          processTenant({
            debug,
            baseUri: hg.clientApiBaseUri,
            apikey,
            env,
            flow,
            tenant,
            connectorId: connector,
            config,
            isV2Flow,
          })
        )
      );

      tenantsUpdated.push(...maybeTenantsUpdated.filter(Boolean));
    }

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    if (json) {
      printJSON(tenantsUpdated);
    } else {
      // generate results table
      const table = new Table({ head: ['Tenant ID'] });
      tenantsUpdated.forEach((t) => table.push([t]));

      // print results
      console.log(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
